import { useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Download, Upload } from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { Spinner } from '@/components/ui/Spinner';
import { rpc } from '@/lib/rpc';
import { downloadUnitTemplate, parseUnitSheet } from './unitSheet';
import { inventoryKeys, useProjects } from './useInventory';

interface ImportProblem {
  row: number;
  unit_code: string | null;
  problems: string[];
}

interface ImportResult {
  imported: boolean;
  reason?: string;
  total: number;
  written?: number;
  errors: number;
  rows: ImportProblem[];
}

/**
 * Excel import for units, with the dry-run preview the phase asks for (#21).
 *
 * All-or-nothing, unlike the lead import. The reason is in the migration: a price
 * list is one coherent document, and half of one is inventory that disagrees with
 * the developer's own sheet — which is how a rep quotes a price the company cannot
 * honour. So the dialog's job is to make the rejected rows findable, not to offer a
 * "import the good ones anyway" escape hatch.
 */
export function ImportUnitsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: projects } = useProjects();

  const [projectId, setProjectId] = useState('');
  const [parsed, setParsed] = useState<Record<string, string>[] | null>(null);
  const [unmapped, setUnmapped] = useState<string[]>([]);
  const [preview, setPreview] = useState<ImportResult | null>(null);
  const [done, setDone] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [parsing, setParsing] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);

  const runImport = useMutation({
    mutationFn: ({ rows, dryRun }: { rows: Record<string, string>[]; dryRun: boolean }) =>
      rpc<ImportResult>('import_units', {
        p_project_id: projectId,
        p_rows: rows,
        p_dry_run: dryRun,
      }),
  });

  const reset = () => {
    setParsed(null);
    setUnmapped([]);
    setPreview(null);
    setDone(null);
    setError(null);
    setFileName(null);
  };

  const close = () => {
    reset();
    setProjectId('');
    onClose();
  };

  const onFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    reset();
    setFileName(file.name);
    setParsing(true);
    try {
      const sheet = await parseUnitSheet(file);
      if (sheet.rows.length === 0) {
        setError(t('leads.import.empty'));
        return;
      }
      if (!sheet.matchedFields.includes('unit_code') || !sheet.matchedFields.includes('type')) {
        setError(t('inventory.import.missingColumns'));
        setUnmapped(sheet.unmappedHeaders);
        return;
      }

      setParsed(sheet.rows);
      setUnmapped(sheet.unmappedHeaders);
      setPreview(await runImport.mutateAsync({ rows: sheet.rows, dryRun: true }));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('common.error'));
    } finally {
      setParsing(false);
    }
  };

  const commit = async () => {
    if (!parsed) return;
    try {
      const result = await runImport.mutateAsync({ rows: parsed, dryRun: false });
      setDone(result);
      void queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('common.error'));
    }
  };

  const rejected = preview && preview.errors > 0;
  const clean = preview && preview.errors === 0;

  return (
    <Modal open={open} title={t('inventory.importUnits')} onClose={close}>
      <div className="flex flex-col gap-4">
        <p className="text-sm text-slate-600">{t('inventory.importIntro')}</p>
        <Alert tone="info">{t('inventory.importStrictNote')}</Alert>

        <Select
          label={t('inventory.chooseProject')}
          placeholder={t('inventory.chooseProject')}
          value={projectId}
          onChange={(e) => {
            setProjectId(e.target.value);
            reset();
          }}
          options={(projects ?? []).map((p) => ({
            value: p.id,
            label: p.area ? `${p.name} — ${p.area}` : p.name,
          }))}
        />

        {projects && projects.length === 0 ? (
          <Alert tone="info">{t('inventory.noProjects')}</Alert>
        ) : null}

        <div className="flex flex-wrap items-center gap-2">
          <label
            className={`inline-flex cursor-pointer items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium ${
              projectId ? 'bg-white text-slate-800 hover:bg-slate-50' : 'bg-slate-50 text-slate-400'
            }`}
          >
            <Upload className="size-4" aria-hidden="true" />
            {fileName ?? t('leads.import.choose')}
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              className="sr-only"
              disabled={!projectId}
              onChange={(e) => void onFile(e)}
            />
          </label>

          <Button variant="ghost" onClick={() => void downloadUnitTemplate()}>
            <Download className="size-4" aria-hidden="true" />
            {t('leads.import.template')}
          </Button>
        </div>

        {parsing || runImport.isPending ? <Spinner /> : null}
        {error ? <Alert tone="error">{error}</Alert> : null}

        {unmapped.length > 0 ? (
          <Alert tone="info">{t('leads.import.unmapped', { headers: unmapped.join('، ') })}</Alert>
        ) : null}

        {rejected ? (
          <div className="flex flex-col gap-2">
            <Alert tone="error">
              {t('inventory.import.rejected', { errors: preview.errors, total: preview.total })}
            </Alert>

            {/* Every rejected row, with its row number, so the person can fix the
                file rather than guess which line upset it. */}
            <div className="max-h-56 overflow-y-auto rounded-lg border border-slate-200">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-slate-50 text-slate-500">
                  <tr>
                    <th scope="col" className="px-2 py-1 text-start">
                      {t('leads.import.row')}
                    </th>
                    <th scope="col" className="px-2 py-1 text-start">
                      {t('inventory.code')}
                    </th>
                    <th scope="col" className="px-2 py-1 text-start">
                      {t('leads.import.problem')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {preview.rows.map((row) => (
                    <tr key={row.row}>
                      <td className="px-2 py-1 tabular-nums text-slate-500">{row.row}</td>
                      <td className="px-2 py-1 text-slate-700">{row.unit_code || '—'}</td>
                      <td className="px-2 py-1 text-red-700">{row.problems.join(' · ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        {clean && !done ? (
          <Alert tone="success">{t('inventory.rowsOk', { count: preview.total })}</Alert>
        ) : null}

        {done ? (
          <Alert tone="success">{t('inventory.import.done', { count: done.written ?? 0 })}</Alert>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={close}>
            {done ? t('common.close') : t('common.cancel')}
          </Button>
          {clean && !done ? (
            <Button loading={runImport.isPending} onClick={() => void commit()}>
              {t('inventory.doImport', { count: preview.total })}
            </Button>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}
